import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import SectionCard from '../components/SectionCard';
import StatCard from '../components/StatCard';

ChartJS.register(
  ArcElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
);

function DashboardPage() {
  const [stats, setStats] = useState({ totalUsers: 0, totalOffers: 0, totalPayments: 0 });
  const [users, setUsers] = useState([]);
  const [incomeSeries, setIncomeSeries] = useState([]);
  const [actionTypeSeries, setActionTypeSeries] = useState([]);
  const [incomeRange, setIncomeRange] = useState({ start_date: '', end_date: '' });
  const [filters, setFilters] = useState({ start_date: '', end_date: '', action_type: 'all' });
  const [selectedPreset, setSelectedPreset] = useState('month');
  const [loading, setLoading] = useState(true);
  const [loadingChart, setLoadingChart] = useState(false);
  const [error, setError] = useState('');

  const formatDate = useCallback((date) => date.toISOString().slice(0, 10), []);

  const getPresetRange = useCallback((preset) => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);

    const start = new Date(end);
    if (preset === 'week') {
      start.setDate(end.getDate() - 6);
    } else if (preset === 'year') {
      start.setDate(end.getDate() - 364);
    } else {
      start.setDate(end.getDate() - 29);
    }

    return {
      start_date: formatDate(start),
      end_date: formatDate(end),
    };
  }, [formatDate]);

  const fetchIncomeData = useCallback(async (selectedFilters) => {
    const searchParams = new URLSearchParams();
    if (selectedFilters.start_date) {
      searchParams.set('start_date', selectedFilters.start_date);
    }
    if (selectedFilters.end_date) {
      searchParams.set('end_date', selectedFilters.end_date);
    }
    searchParams.set('action_type', selectedFilters.action_type || 'all');

    const response = await fetch(`/api/admin/useractions/daily?${searchParams.toString()}`, {
      method: 'GET',
      credentials: 'include'
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.error || 'Не удалось загрузить поступления');
    }
    return payload;
  }, []);

  const fetchActionTypesData = useCallback(async (selectedFilters) => {
    const searchParams = new URLSearchParams();
    if (selectedFilters.start_date) {
      searchParams.set('start_date', selectedFilters.start_date);
    }
    if (selectedFilters.end_date) {
      searchParams.set('end_date', selectedFilters.end_date);
    }

    const response = await fetch(`/api/admin/useractions/action-types?${searchParams.toString()}`, {
      method: 'GET',
      credentials: 'include'
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.error || 'Не удалось загрузить типы действий');
    }
    return payload;
  }, []);

  const loadChartData = useCallback(async (selectedFilters) => {
    setLoadingChart(true);
    setError('');
    try {
      const [incomePayload, actionTypesPayload] = await Promise.all([
        fetchIncomeData(selectedFilters),
        fetchActionTypesData(selectedFilters)
      ]);

      setIncomeSeries(Array.isArray(incomePayload.items) ? incomePayload.items : []);
      setActionTypeSeries(Array.isArray(actionTypesPayload.items) ? actionTypesPayload.items : []);
      setIncomeRange({
        start_date: incomePayload.start_date || '',
        end_date: incomePayload.end_date || ''
      });
    } catch (requestError) {
      setError(requestError.message || 'Не удалось применить фильтры');
    } finally {
      setLoadingChart(false);
    }
  }, [fetchActionTypesData, fetchIncomeData]);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      setError('');

      try {
        const [statsResponse, usersResponse] = await Promise.all([
          fetch('/api/admin/dashboard/stats', {
            method: 'GET',
            credentials: 'include'
          }),
          fetch('/api/admin/users', {
            method: 'GET',
            credentials: 'include'
          })
        ]);

        const statsPayload = await statsResponse.json();
        const usersPayload = await usersResponse.json();

        if (!statsResponse.ok) {
          throw new Error(statsPayload.error || 'Не удалось загрузить статистику дашборда');
        }

        if (!usersResponse.ok) {
          throw new Error(usersPayload.error || 'Не удалось загрузить список пользователей');
        }

        setStats({
          totalUsers: statsPayload.totalUsers || 0,
          totalOffers: statsPayload.totalOffers || 0,
          totalPayments: statsPayload.totalPayments || 0
        });
        setUsers(Array.isArray(usersPayload) ? usersPayload : []);

        const monthRange = getPresetRange('month');
        const defaultFilters = {
          start_date: monthRange.start_date,
          end_date: monthRange.end_date,
          action_type: 'all'
        };
        setSelectedPreset('month');
        setFilters(defaultFilters);
        await loadChartData(defaultFilters);
      } catch (loadError) {
        setError(loadError.message || 'Не удалось загрузить дашборд');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [getPresetRange, loadChartData]);

  const kpiCards = useMemo(
    () => [
      {
        title: 'Пользователи',
        value: String(stats.totalUsers),
        delta: 'из базы данных',
        tone: 'positive'
      },
      {
        title: 'Офферы',
        value: String(stats.totalOffers),
        delta: 'из базы данных',
        tone: 'positive'
      },
      {
        title: 'Выплаты',
        value: String(stats.totalPayments),
        delta: 'из базы данных',
        tone: 'positive'
      },
      {
        title: 'Администраторы',
        value: String(users.filter((user) => user.role === 'admin').length),
        delta: 'из базы данных',
        tone: 'positive'
      }
    ],
    [stats, users]
  );

  const usesAmountMetric = filters.action_type === 'all' || filters.action_type === 'payment';
  const lineMetricKey = usesAmountMetric ? 'total_amount' : 'actions_count';
  const lineMetricLabel = usesAmountMetric ? 'Поступления' : 'Количество действий';

  const lineData = {
    labels: incomeSeries.map((item) => item.date),
    datasets: [
      {
        label: lineMetricLabel,
        data: incomeSeries.map((item) => Number(item[lineMetricKey] || 0)),
        borderColor: '#117f9f',
        backgroundColor: 'rgba(17, 127, 159, 0.18)',
        tension: 0.35,
        fill: true
      }
    ]
  };

  const channelData = {
    labels: actionTypeSeries.map((item) => item.action_type),
    datasets: [
      {
        data: actionTypeSeries.map((item) => Number(item.actions_count || 0)),
        backgroundColor: ['#117f9f', '#f2a900', '#172033'],
        borderWidth: 0
      }
    ]
  };

  const handlePresetSelect = async (preset) => {
    const presetRange = getPresetRange(preset);
    const nextFilters = {
      start_date: presetRange.start_date,
      end_date: presetRange.end_date,
      action_type: filters.action_type
    };

    setSelectedPreset(preset);
    setFilters(nextFilters);
    await loadChartData(nextFilters);
  };

  const handleActionTypeChange = async (nextActionType) => {
    const nextFilters = {
      ...filters,
      action_type: nextActionType
    };
    setFilters(nextFilters);
    await loadChartData(nextFilters);
  };

  const handleDateChange = async (fieldName, value) => {
    const nextFilters = {
      ...filters,
      [fieldName]: value
    };
    setSelectedPreset('custom');
    setFilters(nextFilters);

    if (nextFilters.start_date && nextFilters.end_date) {
      await loadChartData(nextFilters);
    }
  };

  const actionTypeLabels = {
    all: 'Все',
    payment: 'Оплата',
    registration: 'Регистрация',
    other: 'Другое'
  };

  return (
    <div className="page-grid">
      {error ? <p className="offer-error">{error}</p> : null}

      <section className="kpi-grid">
        {kpiCards.map((card) => (
          <StatCard key={card.title} {...card} />
        ))}
      </section>

      <section className="dual-grid">
        <SectionCard
          title="Поступления по дням"
          subtitle={`useractions за период ${incomeRange.start_date || '-'} - ${incomeRange.end_date || '-'} (по умолчанию последний месяц), тип: ${actionTypeLabels[filters.action_type] || 'Все'}, метрика: ${lineMetricLabel}`}
        >
          <div className="dashboard-presets">
            <button
              type="button"
              className={`offer-action-button${selectedPreset === 'week' ? ' active' : ''}`}
              onClick={() => handlePresetSelect('week')}
              disabled={loadingChart}
            >
              Неделя
            </button>
            <button
              type="button"
              className={`offer-action-button${selectedPreset === 'month' ? ' active' : ''}`}
              onClick={() => handlePresetSelect('month')}
              disabled={loadingChart}
            >
              Месяц
            </button>
            <button
              type="button"
              className={`offer-action-button${selectedPreset === 'year' ? ' active' : ''}`}
              onClick={() => handlePresetSelect('year')}
              disabled={loadingChart}
            >
              Год
            </button>
          </div>
          <div className="dashboard-filters">
            <label>
              Дата от
              <input
                type="date"
                value={filters.start_date}
                onChange={(event) => handleDateChange('start_date', event.target.value)}
              />
            </label>
            <label>
              Дата до
              <input
                type="date"
                value={filters.end_date}
                onChange={(event) => handleDateChange('end_date', event.target.value)}
              />
            </label>
            <label>
              Тип действия
              <select
                value={filters.action_type}
                onChange={(event) => handleActionTypeChange(event.target.value)}
              >
                <option value="all">Все</option>
                <option value="payment">Оплата</option>
                <option value="registration">Регистрация</option>
                <option value="other">Другое</option>
              </select>
            </label>
            <div className="dashboard-loading-state">{loadingChart ? 'Обновление графика...' : 'Автообновление включено'}</div>
          </div>
          <Line data={lineData} options={{ responsive: true, plugins: { legend: { display: false } } }} />
        </SectionCard>

        <SectionCard title="Типы действий пользователей" subtitle="Распределение action_type в выбранном периоде">
          <div className="doughnut-wrap">
            <Doughnut data={channelData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
          </div>
        </SectionCard>
      </section>

      <SectionCard title="Последние пользователи" subtitle="Свежие регистрации из базы данных">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Роль</th>
                <th>Статус</th>
                <th>Дата регистрации</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5">Загрузка...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="5">Пользователи не найдены</td>
                </tr>
              ) : (
                users
                  .slice()
                  .sort((a, b) => new Date(b.dataregistr || 0) - new Date(a.dataregistr || 0))
                  .slice(0, 10)
                  .map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.email}</td>
                      <td>{user.role}</td>
                      <td>
                        <span className="status-badge">{user.status}</span>
                      </td>
                      <td>{user.dataregistr ? new Date(user.dataregistr).toLocaleString() : '-'}</td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

export default DashboardPage;